import React, { useState, useEffect, useContext } from 'react';
import { SocketContext } from '../context/SocketContext';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import ChatWindow from '../components/ChatWindow';
import CallModal from '../components/CallModal';
import { useWebRTC } from '../hooks/useWebRTC';

const Home = () => {
  const socket = useContext(SocketContext);
  const { user: currentUser } = useContext(AuthContext);
  const [activeChat, setActiveChat] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [messages, setMessages] = useState([]);
  const [onlineStatuses, setOnlineStatuses] = useState({});
  const [toast, setToast] = useState(null);
  
  // Mobile View Management State: 'sidebar' or 'chat'
  const [mobileView, setMobileView] = useState('sidebar');

  const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const { callState, localStream, remoteStream, initiateCall, acceptCall, declineCall, endCall } = useWebRTC(socket, activeChat, setToast);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${serverUrl}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const structuralData = data.map(c => ({
          ...c,
          participants: c.participants.filter(p => p._id !== currentUser.id)
        }));
        setConversations(structuralData);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const handleSearchChange = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await fetch(`${serverUrl}/api/users/search?query=${query}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) setSearchResults(data);
    } catch (err) {
      console.error(err);
    }
  };

  const openConversation = async (recipientId) => {
    try {
      const res = await fetch(`${serverUrl}/api/messages/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ recipientId })
      });
      const data = await res.json();
      if (res.ok) {
        return data._id;
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  useEffect(() => {
    const loadMessages = async () => {
      if (!activeChat) return;
      let targetConvId = activeChat.conversationId;
      if (!targetConvId) {
        targetConvId = await openConversation(activeChat.user._id);
        if (targetConvId) {
          setActiveChat(prev => ({ ...prev, conversationId: targetConvId }));
        } else {
          return;
        }
      }

      try {
        const res = await fetch(`${serverUrl}/api/messages/${targetConvId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) setMessages(data);
      } catch (err) {
        console.error(err);
      }
    };
    loadMessages();
  }, [activeChat?.user?._id]);

  const handleSendMessage = (text) => {
    if (!activeChat || !socket) return;
    socket.emit('send-message', {
      conversationId: activeChat.conversationId,
      senderId: currentUser.id,
      recipientId: activeChat.user._id,
      text
    });
  };

  useEffect(() => {
    if (!socket) return;

    socket.on('message-sent', (msg) => {
      if (activeChat && activeChat.conversationId === msg.conversationId) {
        setMessages(prev => [...prev, msg]);
      }
      fetchConversations();
    });

    socket.on('message-received', (msg) => {
      if (activeChat && activeChat.conversationId === msg.conversationId) {
        setMessages(prev => [...prev, msg]);
      } else {
        setToast({ message: `New message packaging arriving from remote socket node`, type: 'info' });
      }
      fetchConversations();
    });

    socket.on('user-status-change', ({ userId, status }) => {
      setOnlineStatuses(prev => ({ ...prev, [userId]: status }));
    });

    return () => {
      socket.off('message-sent');
      socket.off('message-received');
      socket.off('user-status-change');
    };
  }, [socket, activeChat]);

  const handleSelectChat = (chatData) => {
    setActiveChat(chatData);
    setMobileView('chat'); 
  };

  return (
    <div className="flex-1 flex overflow-hidden relative h-[calc(100vh-4rem)] w-full">
      {toast && (
        <div className="absolute top-4 right-4 z-50 px-4 py-2.5 rounded-xl shadow-xl text-xs text-white font-medium tracking-wide border backdrop-blur-md bg-blue-600/90 border-blue-700 animate-fade-in">
          {toast.message}
        </div>
      )}

      {/* SIDEBAR CONTAINER
        Mobile: Toggled via state class 'hidden' vs 'block'
        Desktop (md:): Always displayed as a fixed-width left pane 'md:block md:w-80'
      */}
      <div className={`w-full md:w-80 md:block h-full flex-shrink-0 ${mobileView === 'sidebar' ? 'block' : 'hidden'}`}>
        <Sidebar 
          activeChat={activeChat} 
          setActiveChat={handleSelectChat} 
          conversations={conversations} 
          searchResults={searchResults} 
          onSearchChange={handleSearchChange} 
          onlineStatuses={onlineStatuses}
        />
      </div>

      {/* CHAT WINDOW CONTAINER
        Mobile: Toggled via state class 'hidden' vs 'block'
        Desktop (md:): Always filled out 'md:block md:flex-1' right beside the sidebar
      */}
      <div className={`w-full h-full md:flex md:flex-1 ${mobileView === 'chat' ? 'block' : 'hidden'}`}>
        <ChatWindow 
          activeChat={activeChat} 
          messages={messages} 
          onSendMessage={handleSendMessage} 
          onInitiateCall={initiateCall}
          onBack={() => setMobileView('sidebar')}
        />
      </div>

      <CallModal 
        callState={callState} 
        onAccept={acceptCall} 
        onDecline={declineCall} 
        onEndCall={endCall} 
        localStream={localStream} 
        remoteStream={remoteStream}
      />
    </div>
  );
};

export default Home;